/**
 * ClassificationStatsCard — Shows PO classification stats (mainframe/midstack detection)
 * "5 mainframe POs detected (0.5%) | 995 unclassified"
 * Collapsible: breakdown by type, confidence, recent classifications
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, ChevronDown, ChevronRight, RefreshCw, Server, Layers } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './ClassificationStatsCard.css';

function fmt(n) { return parseInt(n || 0).toLocaleString(); }
function fmtSpend(n) {
  const v = parseFloat(n || 0);
  if (v >= 1e9) return `$${(v/1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

function ConfidenceBadge({ level }) {
  const colors = {
    HIGH: { bg: 'rgba(16,185,129,0.15)', text: '#6ee7b7', border: 'rgba(16,185,129,0.3)' },
    MEDIUM: { bg: 'rgba(245,158,11,0.15)', text: '#fcd34d', border: 'rgba(245,158,11,0.3)' },
    LOW: { bg: 'rgba(156,163,175,0.15)', text: '#9ca3af', border: 'rgba(156,163,175,0.3)' },
  };
  const c = colors[level] || colors.LOW;
  return (
    <span className="clcard-confidence" style={{ background: c.bg, color: c.text, borderColor: c.border }}>
      {level}
    </span>
  );
}

function TypeBadge({ isMainframe, isMidstack }) {
  if (isMainframe) {
    return <span className="clcard-type clcard-type--mainframe"><Server size={10} /> Mainframe</span>;
  }
  if (isMidstack) {
    return <span className="clcard-type clcard-type--midstack"><Layers size={10} /> Midstack</span>;
  }
  return <span className="clcard-type clcard-type--other">Other</span>;
}

export default function ClassificationStatsCard({ className }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [deptExpanded, setDeptExpanded] = useState(false);
  const [recentExpanded, setRecentExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/po-classification/summary');
      setData(res);
    } catch (e) {
      console.warn('ClassificationStatsCard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className={`clcard ${className || ''}`}>
      <div className="clcard-loading"><RefreshCw size={16} className="clcard-spin" /> Loading classification stats...</div>
    </div>
  );

  if (!data) return null;

  const mainframePct = data.by_type?.mainframe_pct || 0;
  const mainframeColor = mainframePct > 0 ? '#a78bfa' : '#6b7280';

  return (
    <div className={`clcard ${className || ''}`}>
      {/* Header row */}
      <div className="clcard-header" onClick={() => setExpanded(e => !e)}>
        <div className="clcard-header-left">
          <Cpu size={15} className="clcard-icon" />
          <span className="clcard-title">PO Classification</span>
          <span className="clcard-badge" style={{ background: `${mainframeColor}22`, color: mainframeColor, borderColor: `${mainframeColor}44` }}>
            {fmt(data.by_type?.mainframe || 0)} mainframe
          </span>
          {data.by_type?.midstack > 0 && (
            <span className="clcard-badge clcard-badge--midstack">
              {fmt(data.by_type?.midstack)} midstack
            </span>
          )}
        </div>
        <div className="clcard-header-right">
          <span className="clcard-header-total">{fmt(data.total_classified)} classified</span>
          <button className="clcard-refresh" onClick={e => { e.stopPropagation(); load(); }}>
            <RefreshCw size={12} />
          </button>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="clcard-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Type breakdown */}
            <div className="clcard-stats">
              <div className="clcard-stat clcard-stat--purple">
                <span className="clcard-stat-val">{fmt(data.by_type?.mainframe || 0)}</span>
                <span className="clcard-stat-label">mainframe</span>
              </div>
              <div className="clcard-stat clcard-stat--blue">
                <span className="clcard-stat-val">{fmt(data.by_type?.midstack || 0)}</span>
                <span className="clcard-stat-label">midstack</span>
              </div>
              <div className="clcard-stat">
                <span className="clcard-stat-val">{fmt(data.by_type?.other || 0)}</span>
                <span className="clcard-stat-label">other IT</span>
              </div>
              <div className="clcard-stat">
                <span className="clcard-stat-val">{fmtSpend(data.spend?.mainframe || 0)}</span>
                <span className="clcard-stat-label">mainframe spend</span>
              </div>
            </div>

            {/* Confidence breakdown */}
            <div className="clcard-confidence-section">
              <span className="clcard-section-label">Confidence:</span>
              <div className="clcard-confidence-pills">
                <span className="clcard-conf-pill clcard-conf-high">
                  HIGH: {fmt(data.by_confidence?.high || 0)}
                </span>
                <span className="clcard-conf-pill clcard-conf-medium">
                  MEDIUM: {fmt(data.by_confidence?.medium || 0)}
                </span>
                <span className="clcard-conf-pill clcard-conf-low">
                  LOW: {fmt(data.by_confidence?.low || 0)}
                </span>
              </div>
            </div>

            {/* Recent classifications */}
            {data.recent?.length > 0 && (
              <div className="clcard-recent-section">
                <button className="clcard-section-toggle" onClick={() => setRecentExpanded(e => !e)}>
                  {recentExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  Recent Classifications ({data.recent.length})
                </button>
                <AnimatePresence>
                  {recentExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      className="clcard-recent-list"
                    >
                      {data.recent.map((r, i) => (
                        <div key={i} className="clcard-recent-row">
                          <span className="clcard-recent-po">{r.po_number?.slice(-10)}</span>
                          <TypeBadge isMainframe={r.is_mainframe} isMidstack={r.is_midstack} />
                          <ConfidenceBadge level={r.confidence} />
                          <span className="clcard-recent-supplier" title={r.supplier}>{r.supplier?.slice(0, 20)}</span>
                          <span className="clcard-recent-rationale" title={r.rationale}>{r.rationale}</span>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Per-dept breakdown — fetch on demand */}
            <div className="clcard-dept-section">
              <button className="clcard-section-toggle" onClick={() => setDeptExpanded(e => !e)}>
                {deptExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                By Department
              </button>
              <AnimatePresence>
                {deptExpanded && <DeptBreakdown />}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DeptBreakdown() {
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/api/po-classification/by-dept');
        setDepts(res || []);
      } catch (e) {
        console.warn('DeptBreakdown fetch error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="clcard-dept-loading">
      <RefreshCw size={12} className="clcard-spin" /> Loading...
    </div>
  );

  if (!depts.length) return (
    <div className="clcard-dept-empty">No department data</div>
  );

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="clcard-dept-list"
    >
      <div className="clcard-dept-header-row">
        <span>Dept</span>
        <span>Total</span>
        <span>Mainframe</span>
        <span>Midstack</span>
        <span>Spend</span>
      </div>
      {depts.slice(0, 15).map(d => (
        <div key={d.dept_code} className="clcard-dept-row">
          <span className="clcard-dept-code">{d.dept_code}</span>
          <span className="clcard-dept-total">{fmt(d.total_pos)}</span>
          <span className={`clcard-dept-mf ${d.mainframe > 0 ? 'clcard-dept-mf--has' : ''}`}>
            {d.mainframe > 0 ? fmt(d.mainframe) : '-'}
          </span>
          <span className={`clcard-dept-ms ${d.midstack > 0 ? 'clcard-dept-ms--has' : ''}`}>
            {d.midstack > 0 ? fmt(d.midstack) : '-'}
          </span>
          <span className="clcard-dept-spend">{fmtSpend(d.mainframe_spend)}</span>
        </div>
      ))}
      {depts.length > 15 && (
        <div className="clcard-dept-more">+ {depts.length - 15} more departments</div>
      )}
    </motion.div>
  );
}
